// Copy for the Instagram Reels size checker (/tools/instagram-reels-size), shared by
// frontend/src/views/PublicReelsSizeChecker.vue and middleware.js.
// A paragraph is a list of parts: a string, or { text, href } for a link.
// Numbers come from Meta's pages, checked 2026-11-03 (see frontend/src/services/reelsSize.js).
const HELP_SIZE = 'https://help.instagram.com/1038071743007909';
const HELP_LENGTH = 'https://help.instagram.com/225190788256708';
const ADS_SPEC = 'https://www.facebook.com/business/ads-guide/update/video/instagram-reels';

export default {
  slug: 'instagram-reels-size',
  title: 'Instagram Reels Size Checker: Dimensions and Safe Zone',
  description: 'Check a Reel before you post: its size, aspect ratio, length and the safe zone for text, in pixels for your exact frame. Free, and it runs in your browser.',
  h1: 'Instagram Reels size checker',
  lede: 'Instagram will post most shapes. Yours should be 9:16 at 1080 × 1920 pixels, 3 minutes or less, with its words clear of the buttons. Drop in a video or image, or type its size and length, and this checks all three.',
  ui: {
    crumbHub: 'Tools',
    crumbPage: 'Reels size checker',
    previewAlt: 'Your file inside a 9:16 Reels screen',
    safeBoxLabel: 'Text goes here',
    toggle: 'Show the safe zone',
    dropTitle: 'Choose a video or image',
    dropNote: 'or drop it here. It stays on your device.',
    reading: 'Reading',
    wrongType: 'That isn’t a video or an image. Choose an MP4, MOV, JPG or PNG, or type the size below.',
    unreadable: 'Your browser couldn’t read {file}. Type its width, height and length below instead.',
    width: 'Width (px)',
    widthError: 'Use whole pixels, like 1080.',
    height: 'Height (px)',
    heightError: 'Use whole pixels, like 1920.',
    length: 'Length',
    lengthError: 'Use seconds or minutes:seconds, like 45 or 2:30.',
    status: { pass: 'Fits', warn: 'Check', fail: 'Fix', unknown: 'Not checked' },
    checks: {
      ratio: 'Aspect ratio',
      resolution: 'Resolution',
      length: 'Length',
      sizeMissing: 'Add the width and height to check the shape.',
      resolutionMissing: 'Add the width and height to check the resolution.',
      ratioPass: 'Fills the screen.',
      ratioWarn: 'Instagram takes it, but only 9:16 fills the screen.',
      ratioFail: 'Outside the 1.91:1 to 9:16 range Instagram takes. Crop it to 9:16.',
      resolutionPass: 'At or above the 720 pixel minimum.',
      resolutionFail: 'Under the 720 pixel minimum. Export at 1080 × 1920 or larger.',
      lengthMissing: 'Add the length to check it.',
      lengthBad: 'Write the length as seconds or minutes:seconds, like 45 or 2:30.',
      lengthPass: 'Short enough to be recommended to new viewers.',
      lengthWarn: 'Posts, but Instagram doesn’t recommend Reels over 3 minutes to new viewers.',
      lengthFail: 'Too long. Reels go up to 20 minutes.',
    },
    zoneLabel: 'Safe zone',
    zoneRest: 'Keep text {top} px from the top, {bottom} px from the bottom and {side} px from each side.',
    copy: 'Copy a link to this check',
    copied: 'Link copied',
    copyCite: 'Copy citation',
    citeCopied: 'Citation copied',
    checked: 'Specs as of November 3, 2026',
    cite: 'Instagram Reels size checker, VideoDatabase, https://videodatabase.io/tools/instagram-reels-size',
    ratiosFigure: {
      title: 'Aspect ratios Instagram takes for a Reel',
      desc: 'Five shapes from tall to wide. The tallest, 9:16, is the one that fills the screen; 4:5, 1:1, 16:9 and 1.91:1 post but don’t fill it.',
      fills: 'Fills the screen',
      notFills: 'Doesn’t fill it',
      caption: 'Accepted range from Instagram’s Help Center.',
    },
    zoneFigure: {
      title: 'The Reels safe zone on a 1080 × 1920 frame',
      desc: 'Keep 269 pixels at the top, 672 at the bottom and 65 on each side clear. That leaves a box 950 pixels wide and 979 tall for your words.',
      box: 'Safe box',
      top: 'Top: 269 px (14%)',
      size: 'Safe box: 950 × 979 px',
      sides: 'Sides: 65 px each (6%)',
      bottom: 'Bottom: 672 px (35%)',
      caption: 'Margins from Meta’s Reels ad guide, worked out for a 1080 × 1920 frame.',
    },
  },
  sections: [
    {
      heading: 'Instagram Reels size and dimensions',
      figure: 'ratios',
      paragraphs: [
        ['Export at 1080 × 1920 pixels, 9:16, vertical. Instagram takes any aspect ratio ', { text: 'from 1.91:1 to 9:16', href: HELP_SIZE }, ', so square and landscape videos post too, but 9:16 is the only shape that fills the phone screen.'],
        ['The minimum is 720 pixels and 30 frames per second (fps). The checker doesn’t read frame rate, so set 30 fps or higher when you export. Running the Reel as an ad? Meta’s ad spec asks for ', { text: '1440 × 2560', href: ADS_SPEC }, '.'],
      ],
    },
    {
      heading: 'How long can Instagram Reels be?',
      paragraphs: [
        [{ text: 'Up to 20 minutes', href: HELP_LENGTH }, '. But Instagram won’t recommend a Reel over 3 minutes to new audiences. Posting for your followers? Go long if the video earns it. Posting to reach new people? Cut it to 3:00.'],
      ],
    },
    {
      heading: 'The Reels safe zone, in pixels',
      figure: 'safe-zone',
      paragraphs: [
        ['Instagram puts your caption, handle and buttons on top of the video. Keep ', { text: '14% of the top, 35% of the bottom and 6% on each side', href: ADS_SPEC }, ' free of text and logos. Meta wrote that rule for ads. Use it on every Reel.'],
        ['At 1080 × 1920, that’s 269 pixels at the top, 672 at the bottom and 65 on each side. The box left for your words is 950 pixels wide and 979 tall. Your on-screen text, your face in a talking video and any product shot go inside it.'],
        ['This is where a cropped landscape clip goes wrong. A title that sat in the bottom third of a 16:9 shot lands under the caption and buttons once you crop it to 9:16. Move it up into the box before you export.'],
      ],
    },
    {
      heading: 'Instagram Reels cover size',
      paragraphs: [
        ['Instagram recommends a cover photo of ', { text: '420 × 654 pixels', href: HELP_SIZE }, ' (1:1.55). That’s less tall than the 9:16 Reel, so keep the face or the title in the middle.'],
      ],
    },
    {
      heading: 'See where the words sit in Reels that work',
      paragraphs: [
        ['Open a few high-performing Reels built on a ',{ text: 'text overlay', href: '/elements/text-overlay' }, ', a ', { text: 'title text hook', href: '/elements/title-text-hook' }, ' or ', { text: 'word-by-word captions', href: '/elements/dynamic-word-by-word-captions' }, ' and watch where the words sit and how long they stay up.'],
        ['Scrolling isn’t studying. The videos in the library are broken down by ', { text: 'format', href: '/formats' }, ', concept and ', { text: 'hook', href: '/hooks' }, ', and a free account opens all of it. Find the ones closest to the Reel you’re making, then script yours.'],
      ],
    },
  ],
};
